import type { FC } from "react";
import styled from "styled-components";

export type AdminBillingProvider = "manual" | "gocardless";

const OPTIONS: Array<{
  value: AdminBillingProvider;
  label: string;
  description: string;
}> = [
  {
    value: "manual",
    label: "Manual (Invoice Only)",
    description: "Invoices are created for manual collection.",
  },
  {
    value: "gocardless",
    label: "GoCardless (Direct Debit)",
    description:
      "Invoices are charged via the customer's Direct Debit mandate.",
  },
];

export function isAdminBillingProvider(
  value: unknown
): value is AdminBillingProvider {
  return OPTIONS.some((option) => option.value === value);
}

export function billingProviderLabel(provider: string | null): string {
  return (
    OPTIONS.find((option) => option.value === provider)?.label ??
    provider ??
    "None"
  );
}

const Note = styled.div`
  background: #fff8ec;
  border: 1px solid #f5c878;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  color: #495b6c;
  line-height: 1.5;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Option = styled.div<{ $selected: boolean; $disabled: boolean }>`
  padding: 12px 14px;
  border-radius: 8px;
  border: 2px solid ${({ $selected }) => ($selected ? "#027aff" : "#e0e0e0")};
  background: ${({ $selected }) => ($selected ? "#f0f7ff" : "#fafafa")};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
`;

const OptionTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #13273f;
  margin-bottom: 2px;
`;

const OptionDesc = styled.div`
  font-size: 12px;
  color: #666;
  line-height: 1.4;
`;

interface BillingProviderStepProps {
  /** The company's current "Billing provider permission". */
  currentProvider: string | null;
  value: AdminBillingProvider | null;
  onChange: (provider: AdminBillingProvider) => void;
  disabled?: boolean;
}

/**
 * The company-wide billing provider every (non-Bespoke) subscription follows.
 * The choice is applied to the company when the wizard is saved.
 */
const BillingProviderStep: FC<BillingProviderStepProps> = ({
  currentProvider,
  value,
  onChange,
  disabled = false,
}) => (
  <>
    <Note>
      This is the company&apos;s billing provider (current:{" "}
      <strong>{billingProviderLabel(currentProvider)}</strong>). Changing it
      applies to all of the company&apos;s subscriptions when you save.
    </Note>
    <Options>
      {OPTIONS.map((option) => (
        <Option
          key={option.value}
          $selected={value === option.value}
          $disabled={disabled}
          onClick={() => {
            if (!disabled) onChange(option.value);
          }}
        >
          <OptionTitle>{option.label}</OptionTitle>
          <OptionDesc>{option.description}</OptionDesc>
        </Option>
      ))}
    </Options>
  </>
);

export default BillingProviderStep;
